/**
 * Entry Text Preparation
 *
 * Converts entries into enriched searchable plain-text strings for embedding.
 * Includes type prefix, title in body, type-specific metadata, topic names,
 * people names, and creation date context.
 * Strips markdown, truncates to ~2000 chars, handles all 11 entry types.
 */

import { resolveLinkedPeople } from '@/document'
import type { DocumentView, Entry, EntryType } from '@/document'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_BODY_LENGTH = 2000
// EmbeddingGemma document prefix: "title: none | text: <body>"
// See: https://huggingface.co/google/embeddinggemma-300m
const DOC_PREFIX = 'title: none | text: '

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

// ---------------------------------------------------------------------------
// Markdown stripping
// ---------------------------------------------------------------------------

function stripMarkdown(text: string): string {
  return (
    text
      // Images: ![alt](url) -> alt
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
      // Links: [text](url) -> text
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      // Bold/italic
      .replace(/(\*{1,3}|_{1,3})([^*_]+)\1/g, '$2')
      // Strikethrough
      .replace(/~~([^~]+)~~/g, '$1')
      // Headings
      .replace(/^#{1,6}\s+/gm, '')
      // Blockquotes
      .replace(/^>\s+/gm, '')
      // Inline code
      .replace(/`([^`]+)`/g, '$1')
      // Code blocks
      .replace(/```[\s\S]*?```/g, '')
      // Horizontal rules
      .replace(/^[-*_]{3,}\s*$/gm, '')
      // List markers
      .replace(/^[\s]*[-*+]\s+/gm, '')
      .replace(/^[\s]*\d+\.\s+/gm, '')
      // Extra whitespace
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  )
}

// ---------------------------------------------------------------------------
// Metadata extractors per type
// ---------------------------------------------------------------------------

function safeString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function getMetadataText(entry: Entry): string {
  const type = entry.type as EntryType
  const parts: string[] = []

  switch (type) {
    case 'journal': {
      const mood = safeString(entry.metadata['mood'])
      if (mood) parts.push(`Mood: ${mood}.`)
      const promptText = safeString(entry.metadata['promptText'])
      if (promptText) parts.push(`Reflection prompt: ${promptText}.`)
      break
    }
    case 'task': {
      const status = safeString(entry.metadata['status'])
      if (status) parts.push(`Status: ${status}.`)
      const priority = safeString(entry.metadata['priority'])
      const important = entry.metadata['important'] === true
      if (priority === 'high' || important) parts.push('High priority.')
      break
    }
    case 'book': {
      const author = safeString(entry.metadata['author'])
      if (author) parts.push(`by ${author}.`)
      const status = safeString(entry.metadata['status'])
      if (status) parts.push(`Reading status: ${status}.`)
      break
    }
    case 'movie': {
      const director = safeString(entry.metadata['director'])
      if (director) parts.push(`directed by ${director}.`)
      const status = safeString(entry.metadata['status'])
      if (status === 'want_to_watch') {
        parts.push('Want to watch.')
      } else if (status === 'seen') {
        parts.push('Already watched.')
      } else if (status) {
        parts.push(`Watch status: ${status}.`)
      }
      break
    }
    case 'person':
    case 'artist':
    case 'release':
    case 'label':
      break
    default:
      break
  }

  return parts.join(' ')
}

// ---------------------------------------------------------------------------
// People resolution
// ---------------------------------------------------------------------------

/**
 * The names of the people linked to an entry.
 *
 * This walked `doc.links` itself until #230, under a comment reading "simplified from
 * relationship-resolver.ts" — it was the sixth and last of #290's duplicated projections, and the
 * only one of search's three walks that applied the archived-endpoint check. That check is the
 * view's contract now (ADR 0012 rule 5), so the behaviour is unchanged here and the duplication is
 * gone. `rank.ts`'s two walks are where the rule actually changes what the app returns.
 */
function resolvePersonNames(entryId: string, doc: DocumentView): string[] {
  return resolveLinkedPeople(entryId, doc)
    .filter((person) => person.title)
    .map((person) => person.title)
}

// ---------------------------------------------------------------------------
// Date context
// ---------------------------------------------------------------------------

function getDateContext(createdAt: string): string {
  const date = new Date(createdAt)
  const month = MONTH_NAMES[date.getUTCMonth()]
  const year = date.getUTCFullYear()
  if (month && year) {
    return `Written in ${month} ${year}.`
  }
  return ''
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Convert an entry into natural-language text for embedding.
 * Returns null for archived or empty entries.
 *
 * Uses EmbeddingGemma format: "title: none | text: <natural language body>"
 * Title is always "none" in the prefix; the actual title appears in the body.
 * Avoids bracketed tags -- the model works best with natural prose.
 * See: https://huggingface.co/google/embeddinggemma-300m
 */
export function entryToText(entry: Entry, doc: DocumentView): string | null {
  // Skip archived entries and person entries (people are found by name, not search)
  if (entry.archivedAt) return null
  if (entry.type === 'person') return null

  // Skip entries with no meaningful content
  const titleTrimmed = (entry.title ?? '').trim()
  const contentTrimmed = (entry.content ?? '').trim()
  if (!titleTrimmed && !contentTrimmed) return null

  // Build natural-language body (no bracketed tags -- they confuse the model)
  const parts: string[] = []

  // Type as natural text (e.g. "Journal." / "Book." / "Task.")
  const typeLabel = entry.type.charAt(0).toUpperCase() + entry.type.slice(1)
  parts.push(`${typeLabel}.`)

  // Title as a sentence in the body
  if (titleTrimmed) parts.push(`${titleTrimmed}.`)

  // Type-specific metadata in natural form
  const metaText = getMetadataText(entry)
  if (metaText) parts.push(metaText)

  // Content (stripped of markdown)
  if (contentTrimmed) parts.push(stripMarkdown(contentTrimmed))

  // Topic names as natural text
  const topicNames = entry.topicIds
    .map((id) => doc.topics[id]?.name)
    .filter((n): n is string => Boolean(n))
  if (topicNames.length > 0) parts.push(`Topics: ${topicNames.join(', ')}.`)

  // People names as natural text
  const personNames = resolvePersonNames(entry.id, doc)
  if (personNames.length > 0) parts.push(`About ${personNames.join(', ')}.`)

  // Creation date context
  const dateContext = getDateContext(entry.createdAt)
  if (dateContext) parts.push(dateContext)

  // Join and truncate body
  let body = parts.join(' ')
  if (body.length > MAX_BODY_LENGTH) {
    body = body.slice(0, MAX_BODY_LENGTH)
  }

  return `${DOC_PREFIX}${body}`
}

/**
 * Fast hash for change detection (djb2 algorithm).
 * Not cryptographic -- used only to detect content changes.
 */
export function contentHash(text: string): string {
  let hash = 5381
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0
  }
  return (hash >>> 0).toString(16)
}
